import { useEffect, useMemo, useState } from 'react';
import {
  Video,
  FileText,
  Loader2,
  Play,
  Download,
  RefreshCw,
  Sparkles,
  Headphones,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';

// Zoom cloud-recording artefacts for one booking: audio (stored in our DB),
// clean speaker-wise transcript, AI summary, and the Zoom video link.

export interface MeetingRecordingFields {
  id: string;
  status: string;
  zoomMeetingId?: string | null;
  startedAt?: string | null;
  transcript?: string | null;
  transcriptStatus?: string | null; // 'pending' | 'ready'
  recordingUrl?: string | null;
  audioStatus?: string | null;      // 'ready' | 'failed' | 'too_large'
  aiSummary?: string | null;
  aiSummaryStatus?: string | null;  // 'ready' | 'failed' | 'no_ai'
  guestName: string;
  user: { name: string } | null;
}

interface Cue { time: string; speaker: string; text: string }

// Zoom VTT → [{time, speaker, text}], merging consecutive lines by the same
// speaker. Zoom writes cue text as "Speaker Name: words".
function parseVtt(vtt: string): Cue[] {
  const cues: Cue[] = [];
  for (const block of vtt.replace(/\r/g, '').split(/\n\s*\n/)) {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    const timeIdx = lines.findIndex((l) => l.includes('-->'));
    if (timeIdx === -1) continue;
    const time = lines[timeIdx].split('-->')[0].trim().replace(/\.\d+$/, '').replace(/^00:/, '');
    const body = lines.slice(timeIdx + 1).join(' ');
    if (!body) continue;
    const m = body.match(/^([^:]{1,60}):\s*(.*)$/);
    const speaker = m ? m[1].trim() : '';
    const text = m ? m[2] : body;
    const last = cues[cues.length - 1];
    if (last && last.speaker === speaker) last.text += ` ${text}`;
    else cues.push({ time, speaker, text });
  }
  return cues;
}

export function MeetingRecordingPanel({ leadId, meeting, onChange }: {
  leadId: string;
  meeting: MeetingRecordingFields;
  onChange: () => void;
}) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  // Release the blob URL when the card unmounts / audio reloads.
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const cues = useMemo(() => (meeting.transcript ? parseVtt(meeting.transcript) : []), [meeting.transcript]);

  const hasAnything = !!(meeting.recordingUrl || meeting.transcriptStatus || meeting.audioStatus || meeting.aiSummaryStatus);
  // Fetch is offered for Zoom meetings that have happened (or have partial data).
  const canSync = !!meeting.zoomMeetingId && (meeting.status === 'COMPLETED' || !!meeting.startedAt || hasAnything);
  if (!hasAnything && !canSync) return null;

  const base = `/leads/${leadId}/meetings/${meeting.id}`;

  const fetchAudioBlob = async (): Promise<Blob | null> => {
    // Blob fetch so the axios bearer token is attached.
    const res = await api.get(`${base}/audio`, { responseType: 'blob' });
    return res.data as Blob;
  };

  const playAudio = async () => {
    setLoadingAudio(true);
    try {
      const blob = await fetchAudioBlob();
      if (blob) setAudioUrl(URL.createObjectURL(blob));
    } catch {
      toast('error', 'Could not load the meeting audio.');
    } finally {
      setLoadingAudio(false);
    }
  };

  const downloadAudio = async () => {
    try {
      const blob = audioUrl ? null : await fetchAudioBlob();
      const url = audioUrl ?? (blob ? URL.createObjectURL(blob) : null);
      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.download = `meeting-${meeting.guestName || meeting.id}.m4a`;
      a.click();
      if (!audioUrl) setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast('error', 'Could not download the audio.');
    }
  };

  const downloadTranscript = () => {
    const text = cues.length
      ? cues.map((c) => `[${c.time}] ${c.speaker ? `${c.speaker}: ` : ''}${c.text}`).join('\n')
      : meeting.transcript ?? '';
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${meeting.guestName || meeting.id}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const syncFromZoom = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`${base}/recording/sync`);
      toast(data?.ok ? 'success' : 'error', data?.message ?? 'Done');
      onChange();
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Could not fetch the recording from Zoom.');
    } finally {
      setSyncing(false);
    }
  };

  const regenerateSummary = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post(`${base}/summary`);
      if (data?.aiSummaryStatus === 'ready') toast('success', 'AI summary updated.');
      else if (data?.aiSummaryStatus === 'no_ai') toast('error', 'AI is not set up. Add an API key in AI Assistant → AI Settings.');
      else toast('error', 'AI summary failed. Please try again.');
      onChange();
    } catch {
      toast('error', 'AI summary failed. Please try again.');
    } finally {
      setSummarizing(false);
    }
  };

  const counselorName = meeting.user?.name ?? '';
  const smallBtn = 'inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded disabled:opacity-50';

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Recording</p>
        <div className="flex flex-wrap items-center gap-2">
          {meeting.recordingUrl && (
            <a href={meeting.recordingUrl} target="_blank" rel="noopener noreferrer"
              className={`${smallBtn} text-blue-600 bg-blue-50 hover:bg-blue-100`}>
              <Video className="h-3.5 w-3.5" /> Video on Zoom
            </a>
          )}
          {canSync && (
            <button type="button" onClick={syncFromZoom} disabled={syncing}
              className={`${smallBtn} text-slate-700 bg-white border border-slate-200 hover:bg-slate-100`}>
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Fetch from Zoom
            </button>
          )}
        </div>
      </div>

      {/* Audio */}
      <div>
        <p className="flex items-center gap-1 text-xs font-semibold text-slate-700 mb-1.5">
          <Headphones className="h-3.5 w-3.5" /> Audio
        </p>
        {meeting.audioStatus === 'ready' ? (
          audioUrl ? (
            <div className="flex items-center gap-2">
              <audio controls autoPlay src={audioUrl} className="h-9 w-full max-w-md" />
              <button type="button" onClick={downloadAudio} title="Download audio"
                className="p-1.5 rounded text-slate-500 hover:bg-slate-200">
                <Download className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button type="button" onClick={playAudio} disabled={loadingAudio}
                className={`${smallBtn} text-white bg-blue-600 hover:bg-blue-700`}>
                {loadingAudio ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Play audio
              </button>
              <button type="button" onClick={downloadAudio}
                className={`${smallBtn} text-slate-700 bg-white border border-slate-200 hover:bg-slate-100`}>
                <Download className="h-3.5 w-3.5" /> Download
              </button>
            </div>
          )
        ) : meeting.audioStatus === 'too_large' ? (
          <p className="text-[11px] text-slate-500">Audio is too large to store here — use “Video on Zoom”.</p>
        ) : meeting.audioStatus === 'failed' ? (
          <p className="text-[11px] text-amber-600">Audio download failed. Try “Fetch from Zoom”.</p>
        ) : (
          <p className="text-[11px] text-slate-400">Not available yet. Zoom usually takes a few minutes after the meeting ends.</p>
        )}
      </div>

      {/* AI summary */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="flex items-center gap-1 text-xs font-semibold text-slate-700">
            <Sparkles className="h-3.5 w-3.5 text-violet-500" /> AI summary
          </p>
          {meeting.transcriptStatus === 'ready' && (
            <button type="button" onClick={regenerateSummary} disabled={summarizing}
              className={`${smallBtn} text-violet-700 bg-violet-50 hover:bg-violet-100`}>
              {summarizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {meeting.aiSummaryStatus === 'ready' ? 'Regenerate' : 'Generate'}
            </button>
          )}
        </div>
        {meeting.aiSummaryStatus === 'ready' && meeting.aiSummary ? (
          <div className="whitespace-pre-line rounded-lg border border-violet-100 bg-white p-3 text-xs leading-relaxed text-slate-700">
            {meeting.aiSummary}
          </div>
        ) : meeting.aiSummaryStatus === 'no_ai' ? (
          <p className="text-[11px] text-slate-500">AI is not set up. Add an API key in AI Assistant → AI Settings, then click Generate.</p>
        ) : meeting.aiSummaryStatus === 'failed' ? (
          <p className="text-[11px] text-amber-600">AI summary failed. Click Generate to try again.</p>
        ) : (
          <p className="text-[11px] text-slate-400">Created automatically once the transcript is ready.</p>
        )}
      </div>

      {/* Transcript */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="flex items-center gap-1 text-xs font-semibold text-slate-700">
            <FileText className="h-3.5 w-3.5" /> Transcript
          </p>
          {meeting.transcriptStatus === 'ready' && meeting.transcript && (
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShowTranscript((v) => !v)}
                className={`${smallBtn} text-slate-700 bg-white border border-slate-200 hover:bg-slate-100`}>
                {showTranscript ? 'Hide' : 'View'}
              </button>
              <button type="button" onClick={downloadTranscript} title="Download transcript"
                className={`${smallBtn} text-slate-700 bg-white border border-slate-200 hover:bg-slate-100`}>
                <Download className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        {meeting.transcriptStatus === 'ready' && meeting.transcript ? (
          showTranscript && (
            <div className="max-h-80 overflow-y-auto space-y-2 rounded-lg border border-slate-200 bg-white p-3">
              {cues.length ? cues.map((c, i) => {
                const isCounselor = !!counselorName && c.speaker === counselorName;
                return (
                  <div key={i} className={`rounded-lg px-2.5 py-1.5 text-xs leading-relaxed ${isCounselor ? 'bg-blue-50' : 'bg-slate-50'}`}>
                    <p className="text-[10px] text-slate-400">
                      <span className={`font-semibold ${isCounselor ? 'text-blue-700' : 'text-slate-600'}`}>{c.speaker || 'Speaker'}</span>
                      {' · '}{c.time}
                    </p>
                    <p className="text-slate-700">{c.text}</p>
                  </div>
                );
              }) : (
                <pre className="whitespace-pre-wrap text-[11px] text-slate-700">{meeting.transcript}</pre>
              )}
            </div>
          )
        ) : meeting.transcriptStatus === 'pending' ? (
          <p className="text-[11px] text-slate-400">Zoom is still processing the transcript…</p>
        ) : (
          <p className="text-[11px] text-slate-400">Not available yet. Needs Zoom cloud recording with “Audio transcript” turned on.</p>
        )}
      </div>
    </div>
  );
}
